import { userIsCustomer } from "~/domains/user/utils/roles";
import { Card } from "~/features/configurator/Linear/components/Card";
import classes from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/components/CartSuccessRecommendations/components/ProductEntry.module.less";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import classNames from "classnames";
import { useAddToCartForm } from "~/features/addToCart/hooks/useAddToCartNew";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import { addToCartApi } from "~/features/addToCart/redux/addToCart/actions";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { makeSalesAwarePackCodeProvider } from "~/features/salesCampaign/utils/salesAwarePackCodeProvider";
import { Alert } from "~/common/mui/Alert/Alert";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { FormHelperText, InputLabel, Stack, styled } from "~/common/mui";
import { PriceElements, type PriceProps } from "~/domains/pricing/components/Price/PriceV4";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { calculateSalesCampaign4PDP, useSelectPricingDetails } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { renderButtons } from "~/features/saleRecommendations/hooks/useRecommendationTileButtons";

export interface ProductEntryProps {
  productCode: string;
  reco_type: PurchaseType;
  reco_place: PurchasePlace;
}

export const ProductEntry = ({ productCode, reco_type, reco_place }: ProductEntryProps) => {
  const page = usePurchasePlace();
  const { t } = useTranslation();

  const isTopSeller = reco_type === "top";
  const pnaCallEnabled = !window.SCHA.feature.recoPrice;

  const [formProps, productDescriptor] = useAddToCartForm({
    productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(null, false),
    fetchAvailability: !isTopSeller,
    fetchPrice: pnaCallEnabled,
  });
  const [addedQuantity, setAddedQuantity] = useState(0);

  const selectedPack = formProps.watch("selectedPack");
  const packCode = formProps.watch("selectedPackCode");
  const packSize = selectedPack?.packSize ?? 1;

  const quantity = formProps.watch("quantity");
  const priceReference = formProps.watch("priceReference");

  const salesHelper = useSalesHelper(productDescriptor);

  const isOnSale = salesHelper.isPackageOnSale(packCode);

  const { data } = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor }), [productDescriptor]);
  const { price, oldPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode,
    salesData: data?.salePerPack?.[packCode],
    saleActivated: isOnSale,
  });

  let invalidQuantity = false;
  if (selectedPack) {
    invalidQuantity = quantity === 0 || quantity % selectedPack.packSize > 0;
  }

  const addToCartAction = () => {
    if (quantity) {
      addToCartApi({
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
        ...formProps.getValues(),
        showSuccessDrawer: false,
        addToCartSource: "unknown",
        context: { page, recommendationPlace: reco_place, recommendationType: reco_type },
      }).then(() => {
        setAddedQuantity((prev) => prev + quantity);
      });
    }
  };

  let helperLabel = null;
  if (userIsCustomer()) {
    helperLabel = (
      <span className={classes.helperLabel}>
        {getPackagingLabelAtLineItem({
          t,
          productDescriptor,
          packSize,
          packCode,
          quantity,
        })}
      </span>
    );
  }

  const canSeePrice = userHasPermission(Permission.PRICE);

  const buttonElement = renderButtons(productDescriptor, {
    atc: {
      onClick: addToCartAction,
      disabled: invalidQuantity,
      // @ts-expect-error tracking attribute
      "tracking-aux-action": "add-to-cart",
      sx: { whiteSpace: "nowrap" },
    },
  })[0];

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <Card
        className={classNames(classes.card, userIsCustomer() ? "" : classes.plusUser)}
        tracking-aux-prod-name={productDescriptor.base.designation}
        tracking-aux-prod-id={productDescriptor.base.sapMaterialNumber}
      >
        {isOnSale && <SaleTag size={24} className={classes.salesTag} />}
        <Product.PdpLink reco_type={reco_type}>
          <Product.PrimaryImage className={classes.image} clickable={false} />
        </Product.PdpLink>

        <Stack gap={1} sx={{ gridColumnEnd: { "desktop-l": "span 2" } }}>
          <Stack gap={0.5}>
            <Stack gap={0} direction="row" className={classes.designationContainer}>
              <Product.PreferredIndicator />
              <Product.PdpLink reco_type={reco_type}>
                <Product.Designation as="p" className={classes.designation} clickable={false} />
              </Product.PdpLink>
            </Stack>

            <Product.PdpLink reco_type={reco_type}>
              <Product.ShortDescription clickable={false} />
            </Product.PdpLink>
          </Stack>

          {window.SCHA.feature.recoAvailability && <Product.StockIndicator />}

          {canSeePrice && pnaCallEnabled && (
            <PriceElements.Stack>
              {productDescriptor.isLoading && <CircleSpinner />}
              {!productDescriptor.isLoading && (
                <>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice price={price} zeroText={t("product.priceOnRequest")} variant="bodyLarge" />
                    <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} />
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" />}
                </>
              )}
            </PriceElements.Stack>
          )}
        </Stack>

        <QuantityInput
          min={packSize}
          value={quantity}
          onValueChange={(e) => formProps.setValue("quantity", e)}
          variant="compact"
          step={packSize}
          persistentMinValue
          error={invalidQuantity}
          className={classes.quantity}
          helperLabel={helperLabel}
          disabled={productDescriptor.isLoading && userIsCustomer()}
        />

        <div className={classes.addToCart}>
          <InputLabel className={classes.buttonLabel} shrink={true}>
            &nbsp;
          </InputLabel>
          {buttonElement}
          {addedQuantity > 0 && (
            <FormHelperText>
              <Stack direction={"row"} gap={0.5} sx={{ alignItems: "center" }}>
                <Icon type={IconType.CheckCircleOutline} size={10} />
                {t("addToCart.recommendedProductsAdded", { count: addedQuantity ?? 0 })}
              </Stack>
            </FormHelperText>
          )}
        </div>

        {invalidQuantity && (
          <Alert className={classes.quantityError} severity="error">
            {`${t("newContract.quantityErrorTitle")}. ${t("newContract.quantityErrorMessage")}`}
          </Alert>
        )}
      </Card>
    </ProductDescriptorContext>
  );
};

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
